import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@clerk/react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, ChevronDown, ChevronLeft, ChevronRight, Trash2 } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import Shell from "@/components/Shell";
import FlowSteps from "@/components/FlowSteps";
import ComplianceItem from "@/components/ComplianceItem";
import ConfirmDelete from "@/components/ConfirmDelete";
import EmergencyContactCard from "@/components/EmergencyContactCard";
import DrugTestingCard from "@/components/DrugTesting";
import { makeApi } from "@/lib/api";
import { useRole } from "@/contexts/RoleContext";
import { expectedItems, docStatus, DOC_STATUS, expiryAlerts, missingItems } from "@/lib/compliance";

export function ExpiryAlerts({ alerts }) {
  if (!alerts.length) return null;
  return (
    <div className="brutal-border border-[#FFD600]/50 bg-[#FFD600]/10 p-4 mb-8" data-testid="expiry-alerts">
      <div className="font-heading font-bold mb-2 flex items-center gap-2"><AlertTriangle size={16} className="text-[#FFD600]" /> Documents needing attention</div>
      <ul className="space-y-1 text-sm">
        {alerts.map((a) => (
          <li key={a.id} className={a.status === "expired" ? "text-[#F87171]" : "text-[#FFD600]"} data-testid={`alert-${a.id.replace(/[^a-z0-9]+/gi, "-")}`}>{a.text}</li>
        ))}
      </ul>
    </div>
  );
}

function Groups({ profile, records, api, base, onChanged, onDeleteDriver }) {
  const byId = Object.fromEntries(records.map((r) => [r.item_id, r]));
  return expectedItems(profile).map((g) => (
    <section key={g.key} className="brutal-border mb-6" data-testid={`compliance-group-${g.key}`}>
      <div className="px-4 py-3 border-b border-[#262626] bg-[#141414] flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="font-heading font-bold break-words">{g.title}</div>
          <div className="font-mono-label text-[10px] text-muted-foreground">{g.subtitle}</div>
        </div>
        {onDeleteDriver && g.driverId && <DeleteDriverButton name={g.title} onConfirm={() => onDeleteDriver(g.driverId)} tid={g.key} />}
      </div>
      {g.driverId && <EmergencyContactCard contact={g.contact} tid={g.key} editable={!!onDeleteDriver} />}
      {g.items.map((it) => <ComplianceItem key={it.id} item={{ ...it, notifyLabel: g.key === "company" || g.key === "vehicles" ? it.label : `${g.title} – ${it.label}` }} rec={byId[it.id]} api={api} base={base} onChanged={onChanged} />)}
    </section>
  ));
}

function DeleteDriverButton({ name, onConfirm, tid }) {
  return (
    <ConfirmDelete testid={`delete-${tid}`} onConfirm={onConfirm} confirmLabel="Delete driver" title={`Delete ${name}?`}
      description="This removes the driver and all of their documents: license photos, CDL renewal and medical card. You'll have a few seconds to undo.">
      <button data-testid={`delete-${tid}`}
        className="shrink-0 min-h-[40px] px-3 border border-[#7F1D1D] text-[#F87171] hover:bg-[#DC2626] hover:border-[#DC2626] hover:text-white text-xs font-mono-label inline-flex items-center gap-1.5 transition-colors">
        <Trash2 size={14} /> Delete driver
      </button>
    </ConfirmDelete>
  );
}

function ClientView({ api }) {
  const [profile, setProfile] = useState(null);
  const [records, setRecords] = useState([]);
  const load = useCallback(async () => {
    const [p, c] = await Promise.all([api.get("/profile"), api.get("/compliance")]);
    setProfile(p.data);
    setRecords(c.data.items);
  }, [api]);
  useEffect(() => { load(); }, [load]);
  const { hash } = useLocation();
  const ready = !!profile;
  useEffect(() => {
    if (ready && hash === "#drug-testing") document.getElementById("drug-testing")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [hash, ready]);
  const deleteDriver = async (id) => {
    try {
      const { data } = await api.delete(`/compliance/drivers/${encodeURIComponent(id)}`);
      load();
      const undo = async () => {
        try {
          await api.post(`/compliance/drivers/restore/${data.undo_token}`);
          toast.success("Driver restored");
          load();
        } catch {
          toast.error("Could not restore the driver.");
        }
      };
      toast.success("Driver deleted", data.undo_token ? { duration: 8000, action: { label: "Undo", onClick: undo } } : undefined);
    } catch {
      toast.error("Could not delete the driver.");
    }
  };
  if (!profile) return <div className="font-mono-label text-xs text-muted-foreground">LOADING…</div>;
  const { total, missing } = missingItems(profile, records);
  return (
    <>
      <CompletionMeter done={total - missing.length} total={total} />
      <ExpiryAlerts alerts={expiryAlerts(profile, records)} />
      {!(profile.drivers || []).length && <p className="text-sm text-muted-foreground mb-6">Add drivers in the setup wizard to track their licenses, CDL and medical cards here.</p>}
      <DrugTestingCard profile={profile} api={api} onChanged={load} />
      <Groups profile={profile} records={records} api={api} base="/compliance" onChanged={load} onDeleteDriver={deleteDriver} />
      <PageNav nextMissing={missing[0]} />
    </>
  );
}

function CompletionMeter({ done, total }) {
  const pct = total ? Math.round((done / total) * 100) : 0;
  return (
    <div className="brutal-border p-4 mb-8" data-testid="completion-meter">
      <div className="flex items-baseline justify-between mb-3 gap-3">
        <span className="font-mono-label text-[10px] text-muted-foreground">DOCUMENTS COMPLETE</span>
        <span className="font-heading font-black text-2xl" data-testid="completion-count">{done}/{total} <span className="text-sm text-[#FFD600]">{pct}%</span></span>
      </div>
      <div className="h-2 bg-[#262626]">
        <div className={`h-full transition-[width] duration-500 ${pct === 100 ? "bg-[#16A34A]" : "bg-[#FFD600]"}`} style={{ width: `${pct}%` }} data-testid="completion-bar" />
      </div>
    </div>
  );
}

function focusItem(it) {
  const el = document.querySelector(`[data-testid="compliance-item-${it.id.replace(/[^a-z0-9]+/gi, "-")}"]`);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "center" });
  el.classList.add("ring-2", "ring-[#FFD600]");
  setTimeout(() => el.classList.remove("ring-2", "ring-[#FFD600]"), 2500);
  const input = el.querySelector('input[type="date"]');
  if (input) input.focus({ preventScroll: true });
}

function PageNav({ nextMissing, backTo = "/dashboard", nextTo = "/pricing" }) {
  const navigate = useNavigate();
  const { t } = useLang();
  const shown = useRef(null);
  const back = () => navigate(backTo);
  const saveContinue = () => {
    if (document.activeElement) document.activeElement.blur();
    if (nextMissing && shown.current !== nextMissing.id) {
      shown.current = nextMissing.id;
      toast.success(`Saved. Next: ${nextMissing.notifyLabel || nextMissing.label}`);
      focusItem(nextMissing);
      return;
    }
    toast.success(nextMissing ? "Saved. You can finish your documents anytime." : "All documents saved.");
    navigate(nextTo);
  };
  return (
    <div className="flex items-center justify-between mt-12 gap-4" data-testid="compliance-nav">
      <button onClick={back} data-testid="compliance-back" className="px-5 py-3 border border-[#262626] hover:border-white font-mono-label text-xs inline-flex items-center gap-2 transition-colors">
        <ChevronLeft size={14} /> {t("back")}
      </button>
      <button onClick={saveContinue} data-testid="compliance-save-continue" className="cta-primary px-6 py-3 font-mono-label text-xs inline-flex items-center gap-2">
        {t("saveContinue")} <ChevronRight size={14} />
      </button>
    </div>
  );
}

function clientRows(c) {
  const byId = Object.fromEntries(c.items.map((r) => [r.item_id, r]));
  return expectedItems(c.profile).flatMap((g) => g.items.filter((i) => i.dated).map((i) => ({
    who: g.key === "company" || g.key === "vehicles" ? "" : g.title, label: i.label, status: docStatus(byId[i.id]?.expires), expires: byId[i.id]?.expires,
  })));
}

function AdminView({ api }) {
  const [clients, setClients] = useState(null);
  const [open, setOpen] = useState(null);
  const load = useCallback(() => api.get("/admin/compliance").then(({ data }) => setClients(data.clients)), [api]);
  useEffect(() => { load(); }, [load]);
  if (!clients) return <div className="font-mono-label text-xs text-muted-foreground">LOADING…</div>;
  const all = clients.map((c) => ({ c, rows: clientRows(c) }));
  const count = (s) => all.reduce((n, x) => n + x.rows.filter((r) => r.status === s).length, 0);
  return (
    <>
      <div className="brutal-border grid grid-cols-2 sm:grid-cols-4 mb-8" data-testid="admin-compliance-summary">
        {["current", "expiring", "expired", "none"].map((s) => (
          <div key={s} className="p-4 border-r border-b border-[#262626]">
            <span className={`font-mono-label text-[10px] px-2 py-1 ${DOC_STATUS[s].cls}`}>{DOC_STATUS[s].label}</span>
            <div className="font-heading font-black text-3xl mt-3" data-testid={`summary-${s}`}>{count(s)}</div>
          </div>
        ))}
      </div>
      {all.length === 0 && <p className="text-sm text-muted-foreground">No clients yet.</p>}
      {all.map(({ c, rows }) => {
        const uid = c.profile.user_id;
        const isOpen = open === uid;
        return (
          <section key={uid} className="brutal-border mb-4" data-testid={`admin-compliance-client-${uid}`}>
            <button onClick={() => setOpen(isOpen ? null : uid)} className="w-full text-left px-4 py-3 bg-[#141414] flex items-center gap-3 min-h-[56px]" data-testid={`admin-compliance-toggle-${uid}`}>
              {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              <div className="flex-1 min-w-0">
                <div className="font-heading font-bold truncate">{c.profile.legal_name || c.profile.name || c.profile.email || "Client"}</div>
                <div className="text-xs text-muted-foreground truncate">{c.profile.email}</div>
              </div>
              <div className="flex gap-1">
                {["expired", "expiring", "current"].map((s) => {
                  const n = rows.filter((r) => r.status === s).length;
                  return n ? <span key={s} className={`font-mono-label text-[10px] px-2 py-1 ${DOC_STATUS[s].cls}`}>{n}</span> : null;
                })}
              </div>
            </button>
            {!isOpen && (
              <div className="divide-y divide-[#262626]">
                {rows.filter((r) => r.label === "Medical card" || r.status === "expired" || r.status === "expiring").map((r, i) => (
                  <div key={i} className="px-4 py-2 text-sm flex justify-between gap-3">
                    <span className="min-w-0 truncate">{r.who ? `${r.who} · ` : ""}{r.label}</span>
                    <span className={`font-mono-label text-[10px] px-2 py-1 shrink-0 ${DOC_STATUS[r.status].cls}`}>{DOC_STATUS[r.status].label}</span>
                  </div>
                ))}
              </div>
            )}
            {isOpen && <div className="p-3"><Groups profile={c.profile} records={c.items} api={api} base={`/admin/compliance/${uid}`} onChanged={load} /></div>}
          </section>
        );
      })}
    </>
  );
}

export default function Compliance() {
  const { getToken } = useAuth();
  const api = useMemo(() => makeApi(getToken), [getToken]);
  const { actingAsAdmin } = useRole();
  return (
    <Shell>
      <FlowSteps current={1} />
      <div className="mb-8">
        <div className="font-mono-label text-xs text-[#FFD600] mb-2">{actingAsAdmin ? "OWNER · ALL CLIENTS" : "COMPLIANCE TRACKER"}</div>
        <h1 className="font-heading font-black text-3xl sm:text-5xl leading-none" data-testid="compliance-title">Documents & Expirations.</h1>
        <p className="text-sm text-muted-foreground mt-3">Green is current, yellow expires within 60 days, red is expired.</p>
      </div>
      {actingAsAdmin ? <><AdminView api={api} /><PageNav backTo="/admin" nextTo="/admin" /></> : <ClientView api={api} />}
    </Shell>
  );
}
